import QRCode from "qrcode.react";
import { MouseEvent, useMemo } from "react";

import { LISBlock, LISCase, LISSpecimen } from "pwapi";

import { useAppSelector } from "app/hooks";

import ContextMenu from "./ContextMenu";

type ObjectType = "Specimen" | "Block" | "Slide";

export const BlockHierarchy = ({
  block,
  specimen,
  pathCase,
  onClickFilter = () => {},
  showBarcodes = false,
  showAdminLinks = false,
}: {
  block: LISBlock;
  specimen: LISSpecimen;
  pathCase: LISCase;
  onClickFilter?: (
    event: MouseEvent<HTMLElement>,
    type: ObjectType,
    id: number
  ) => void;
  showBarcodes?: boolean;
  showAdminLinks?: boolean;
}) => {
  const stains = useAppSelector((state) => state.stains);

  const blockSlides = pathCase.slides
    .filter((slide) => slide.block_obj_id === block.id)
    .sort(
      (a, b) =>
        parseInt(a.slide_number || "0") - parseInt(b.slide_number || "0")
    );
  const blockStainIds = [
    ...new Set(blockSlides.map((slide) => slide.stain_id)),
  ].sort();

  return (
    <div className="hierarchy-item">
      <div className="d-flex">
        <h3>
          Block{" "}
          <a
            className="header-value"
            href={`#Block-${block.id}`}
            id={`Block-${block.id}`}
            onClick={(event: MouseEvent<HTMLElement>) =>
              onClickFilter(event, "Block", block.id || 0)
            }
          >
            {specimen.name}
            {block.name}
          </a>
          {showAdminLinks && (
            <ContextMenu
              caseId={pathCase.id || 0}
              type="Block"
              id={block.id || 0}
            />
          )}
          {" - "}
          <code className="lis-state">{block.lis_state}</code>
        </h3>
        <div>
          {showBarcodes && (
            <QRCode
              className="barcode"
              value={`block-${block.id}`}
              size={100}
              renderAs="svg"
            />
          )}
        </div>
      </div>
      <div className="StainHierarchy hierarchy-items">
        {blockStainIds.map((stainId) => (
          <div key={stainId} className="hierarchy-item">
            <div>
              {stains[stainId || 0]?.name}
              {stains[stainId || 0]?.name !==
                stains[stainId || 0]?.abbreviation &&
                ` (${stains[stainId || 0].abbreviation})`}
              {" - "}
              {stains[stainId || 0]?.category}
            </div>
            <div className="SlideHierarchy hierarchy-items">
              {blockSlides
                .filter((slide) => slide.stain_id === stainId)
                .map((slide) => (
                  <div key={slide.id} className="hierarchy-item">
                    <div>
                      <h4>
                        <a
                          className="header-value"
                          href={`#Slide-${slide.id}`}
                          id={`Slide-${slide.id}`}
                          onClick={(event: MouseEvent<HTMLElement>) =>
                            onClickFilter(event, "Slide", slide.id || 0)
                          }
                        >
                          {specimen.name}
                          {block.name} {stains[stainId || 0]?.abbreviation} S
                          {slide.slide_number}
                        </a>
                        {showAdminLinks && (
                          <ContextMenu
                            caseId={pathCase.id || 0}
                            type="Slide"
                            id={slide.id || 0}
                          />
                        )}
                      </h4>
                      <code className="lis-state">{slide.lis_state}</code>
                    </div>
                    {showBarcodes && (
                      <div>
                        <QRCode
                          className="barcode"
                          value={`slide-${slide.id}`}
                          size={80}
                          renderAs="svg"
                        />
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const CaseHierarchy = ({
  pathCase,
  onClickFilter = () => {},
  showBarcodes = false,
  showAdminLinks = false,
}: {
  pathCase: LISCase;
  onClickFilter?: (
    event: MouseEvent<HTMLElement>,
    type: ObjectType,
    id: number
  ) => void;
  showBarcodes?: boolean;
  showAdminLinks?: boolean;
}) => {
  const sortedSpecimens = useMemo(
    () =>
      [...(pathCase?.specimens || [])].sort(
        (a, b) => (a.id || 0) - (b.id || 0)
      ),
    [pathCase?.specimens]
  );

  return (
    <div className="CaseHierarchy SpecimenHierarchy hierarchy-items">
      {sortedSpecimens.map((specimen) => (
        <div key={specimen.id} className="hierarchy-item">
          <div className="d-flex">
            <h2>
              Specimen{" "}
              <a
                className="header-value"
                href={`#Specimen-${specimen.id}`}
                id={`Specimen-${specimen.id}`}
                onClick={(event: MouseEvent<HTMLElement>) =>
                  onClickFilter(event, "Specimen", specimen.id || 0)
                }
              >
                {specimen.name}
              </a>
              {showAdminLinks && (
                <ContextMenu
                  caseId={pathCase.id || 0}
                  type="Specimen"
                  id={specimen.id || 0}
                />
              )}
              {" - "}
              <code className="lis-state">{specimen.lis_state}</code>
            </h2>
            {showBarcodes && (
              <QRCode
                className="barcode"
                value={`specimen-${specimen.id}`}
                size={120}
                renderAs="svg"
              />
            )}
          </div>
          <div className="BlockHierarchy hierarchy-items">
            {pathCase.blocks
              .filter((block) => block.specimen_id === specimen.id)
              .map((block) => (
                <BlockHierarchy
                  block={block}
                  specimen={specimen}
                  pathCase={pathCase}
                  key={block.id}
                  onClickFilter={onClickFilter}
                  showBarcodes={showBarcodes}
                  showAdminLinks={showAdminLinks}
                />
              ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default CaseHierarchy;
